import { Card, CardContent, CardHeader, CardTitle } from 'Assessment/src/components/ui/card';
import { Badge } from 'Assessment/src/components/ui/badge';
import { Progress } from 'Assessment/src/components/ui/progress';
import { Alert, AlertDescription } from 'Assessment/src/components/ui/alert';
import { Loader2, AlertCircle } from 'lucide-react';

interface TranscriptCardProps {
  transcript: string;
  wordCount: number;
  isValid?: boolean;
  validationMessage?: string;
  isProcessing?: boolean;
  processingStage?: 'transcribing' | 'validating' | 'saving';
  attemptCount?: number;
  maxAttempts?: number;
  processingProgress?: number;
}

export const TranscriptCard = ({ 
  transcript, 
  wordCount, 
  isValid,
  validationMessage,
  isProcessing,
  processingStage = 'transcribing',
  attemptCount = 1,
  maxAttempts = 3,
  processingProgress = 0
}: TranscriptCardProps) => {
  const hasMeaningfulTranscript = Boolean(transcript && transcript.replace(/[\s.]/g, '').trim().length > 0);
  const displayTranscript = transcript?.trim() || '';

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Your Response</CardTitle>
          <div className="flex items-center gap-2">
            {attemptCount >= 1 && (
              <Badge variant="secondary" className="text-white/100 text-lg
    font-semibold">
                Attempt {attemptCount} of {maxAttempts}
              </Badge>
            )}
            {isProcessing && (
              <Badge variant="outline" className="gap-1 text-white/100 text-lg
    font-semibold">
                <Loader2 className="h-3 w-3 animate-spin" />
                {processingStage === 'validating' ? 'Validating...' : 'Processing...'}
              </Badge>
            )}
          </div>
        </div>
        {isProcessing && (
          <div className="pt-2 space-y-1">
            <Progress value={processingProgress} className="h-2 bg-muted [&>div]:bg-primary" />
            <p 
            // className="text-xs text-muted-foreground text-right"
            className="text-xs text-white/100 text-right text-lg
    font-semibold" >
              {Math.round(processingProgress)}%</p>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {transcript ? (
          <>
            <div className="min-h-[120px] max-h-[300px] overflow-y-auto p-4 bg-muted rounded-lg border border-border/60">
              <p className="text-lg leading-relaxed whitespace-pre-wrap text-foreground break-words">
                {displayTranscript}
              </p>
            </div>
            {!hasMeaningfulTranscript && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-foreground">
                Speech was detected, but the transcript is low confidence. Please try again with a clearer recording.
              </div>
            )}
            {isValid === false && validationMessage && (
              <Alert variant="destructive" className="border-destructive/50 bg-destructive/10 dark:bg-destructive/20 dark:border-destructive/40">
                <AlertCircle className="h-4 w-4 text-destructive text-lg
    font-semibold dark:text-white" />
                <AlertDescription className="text-lg
    font-semibold text-destructive dark:text-white"
    style={{paddingTop:'1rem'}}>
                  {validationMessage}
                </AlertDescription>
              </Alert>
            )}
          </>
        ) : (
          <div className="min-h-[80px] flex items-center justify-center text-muted-foreground">
            <p className="text-lg">Your transcript will appear here after recording...</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
